/**
 * Shrinks a phone photo before it is queued.
 *
 * A modern phone produces 3–8 MB per shot. Queued in IndexedDB as base64 that
 * becomes ~10 MB, and uploading it over rural 3G costs the technician time and
 * their own data bundle. 1600px on the long edge is plenty to read a serial
 * number off an ONT or show a cut on a pole.
 *
 * Falls back to the original if anything goes wrong — a large photo is better
 * than no photo.
 */
const MAX_EDGE = 1600;
const QUALITY = 0.72;

export async function downscale(file: File): Promise<{ dataUrl: string; mimeType: string }> {
  const original = await readAsDataUrl(file);

  try {
    const image = await loadImage(original);
    const scale = Math.min(1, MAX_EDGE / Math.max(image.width, image.height));

    // Already small enough — re-encoding would only lose detail.
    if (scale === 1 && file.size < 1_000_000) {
      return { dataUrl: original, mimeType: file.type || 'image/jpeg' };
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);

    const context = canvas.getContext('2d');
    if (!context) return { dataUrl: original, mimeType: file.type || 'image/jpeg' };

    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    // Always JPEG out: a 12MP PNG from some Android cameras is enormous, and
    // nothing here needs transparency.
    return { dataUrl: canvas.toDataURL('image/jpeg', QUALITY), mimeType: 'image/jpeg' };
  } catch {
    return { dataUrl: original, mimeType: file.type || 'image/jpeg' };
  }
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('could not decode the image'));
    image.src = dataUrl;
  });
}
